import { Injectable } from "@angular/core";
import {
    asyncScheduler,
    concat,
    Observable,
    pipe,
    range,
    SchedulerLike,
    throwError,
    timer,
} from "rxjs";
import {
    concatMap,
    delay,
    dematerialize,
    map,
    materialize,
    mergeMap,
    retryWhen,
    switchMap,
    take,
} from "rxjs/operators";
import { ApiService } from "../../../shared/api.service";
import { SlideshowService } from "../../../slideshow/slideshow.service";

@Injectable()
export class MlGeneration {
    _statusResult = [];
    get statusResult() {
        return this._statusResult;
    }

    constructor(private api: ApiService) {}

    async getContent(
        overallTopic: string,
        specificTopic: string,
        header: string,
        index: number,
        slideshow: SlideshowService
    ) {
        const userPrompt =
            overallTopic + " ,,, " + specificTopic + " ,,, " + header;
        const basePrompt = `
You are a writer for an online listicle publisher.
You write engaging content based on the request given.
You will write a paragraph expanding on the topic below for an item in the listicle.
The paragraph must be between ${slideshow.assignment.slideWordCount} and ${
            slideshow.assignment.slideWordCount + 25
        } words long.
You will be given the overall topic followed by the specific topic to write about and then finally the header for this paragraph.
Each will be delimited by triple commas.
Write a paragraph for the prompt delimited by triple backticks.

\`\`\`[input]\`\`\``;

        const prompt = this.buildPrompt(basePrompt, userPrompt);

        const content = await this.getResponse(
            prompt,
            "Generating content for slide " + index,
            (response) => {
                let contentOut = response.replace(/`/g, "");

                return contentOut;
            }
        );

        return content.split("\n").filter((line) => line.length > 0);
    }

    async getIntro(userPrompt: string, slideshow: SlideshowService) {
        const basePrompt = `
You are a writer for an online listicle publisher.
You write engaging content based on the request given.
You will write an introduction to the topic that will introduce the idea and get a reader to want to continue to read.
The introduction must be between ${slideshow.assignment.introWordCount} and ${
            slideshow.assignment.introWordCount + 25
        } words long.
Write an introduction for the prompt delimited by triple backticks.

\`\`\`[input]\`\`\``;

        const prompt = this.buildPrompt(basePrompt, userPrompt);

        const intro = await this.getResponse(
            prompt,
            "Generating intro",
            (response) => {
                let introOut = response.replace(/`/g, "");

                return introOut;
            }
        );

        return intro.split("\n").filter((line) => line.length > 0);
    }

    async getTitle(userPrompt: string) {
        const basePrompt = `
You are a writer for an online listicle publisher.
You write engaging content based on the request given.
You will write an title for an article based on a given prompt.
Do not include reference to the number of items included in the listicle.
The title should get a reader to want to click and read more.
The prompt is delimited by triple backticks.

\`\`\`[input]\`\`\``;

        const prompt = this.buildPrompt(basePrompt, userPrompt);

        const title = await this.getResponse(
            prompt,
            "Generating title",
            (response) => {
                let titleOut = response.replace(/`/g, "");
                titleOut = titleOut.replace("\n", "");
                titleOut = titleOut.trim();
                titleOut = this.trim(titleOut, '"');

                return titleOut;
            }
        );

        return title;
    }

    async getHeader(overallTopic: string, example: string, index: number) {
        const userPrompt = overallTopic + " ,,, " + example;
        const basePrompt = `
You are a writer for an online listicle publisher.
You write engaging content based on the request given.
For a given example, you will generate a catchy header.
The header must fit as a title for a subsection in an overall article.
You will be given the overall topic followed by the specific example to write a header for.
Each example will be delimited by triple commas.

\`\`\`[input]\`\`\``;

        const prompt = this.buildPrompt(basePrompt, userPrompt);

        const header = await this.getResponse(
            prompt,
            "Generating header " + index,
            (response) => {
                let headerOut = response.replace(/`/g, "");
                headerOut = headerOut.replace("\n", "");
                headerOut = headerOut.trim();
                headerOut = this.trim(headerOut, '"');

                return headerOut;
            }
        );

        return header;
    }

    async getExamples(userPrompt: string, slideshow: SlideshowService) {
        let numSlides = Math.max(
            slideshow.assignment.slideCount,
            slideshow.slides.length
        );
        if (slideshow.assignment.intro) {
            numSlides = Math.max(
                slideshow.assignment.slideCount,
                slideshow.slides.length - 1
            );
        }

        const basePrompt = `
You are a writer for an online listicle publisher.
You write engaging content based on the request given.
You will come up with relative examples for a given prompt.
Generate a set of ${numSlides} examples for the prompt delimited by triple backticks.
Each example must be delimited by a new line.
Each example must not include backticks.
Each example must not be numbered.
Each new line must include only the generated example.

\`\`\`[input]\`\`\``;

        const prompt = this.buildPrompt(basePrompt, userPrompt);

        const examples = await this.getResponse(
            prompt,
            "Generating examples",
            (response) => {
                let examplesOutString = response.replace(/`/g, "");
                examplesOutString = examplesOutString.replace("\n", "");

                return examplesOutString;
            },
            true
        );

        return examples.split("\n").filter((line) => line.length > 0).slice(0, numSlides);
    }

    buildPrompt(prefixPrompt: string, userPrompt: string) {
        return prefixPrompt.replace("[input]", userPrompt).trim();
    }

    async getResponse(
        prompt: string,
        status: string,
        normalizeFunction: (response: string) => string,
        json: boolean = false
    ) {
        this.statusResult.unshift({
            text: status,
            error: false,
            done: false,
        });

        let responseOut;
        try {
            const rawResponse = await this.getAiResult(prompt, json);

            responseOut = normalizeFunction(rawResponse[0]);
        } catch (error) {
            this.updateStatus("error");
            console.error(error);
            throw error;
        }

        this.updateStatus("done");

        return responseOut;
    }

    async getAiResult(
        prompt: string,
        json: boolean = false,
        n: number = 1
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            this.api
                .doAction("post", "mlcontent", {
                    prompt: prompt,
                    n: n,
                    user: this.api.currentUserId.toString(),
                    json: json,
                })
                .pipe(this.backoff(10))
                .subscribe(
                    (response) => {
                        if (response && response.status === "error") {
                            reject(response);
                        }
                        resolve(response);
                    },
                    (error) => {
                        reject(error);
                    }
                );
        });
    }

    trim(str, ch) {
        var start = 0,
            end = str.length;

        while (start < end && str[start] === ch) ++start;

        while (end > start && str[end - 1] === ch) --end;

        return start > 0 || end < str.length ? str.substring(start, end) : str;
    }

    updateStatus(status: string) {
        if (status === "error") {
            this.statusResult[0].error = true;
        }

        if (status === "done") {
            this.statusResult[0].done = true;
        }
        this._statusResult = [...this.statusResult];
    }

    backoff(maxRetries = 5): (_: Observable<any>) => Observable<any> {
        return pipe(
            retryWhen((errors) =>
                errors.pipe(
                    mergeMap((error, i) => {
                        const retryAttempt = i + 1;
                        if (retryAttempt > maxRetries) {
                            return throwError(error);
                        } else {
                            const waitms = retryAttempt * retryAttempt * 1000;
                            console.debug(
                                `Attempt ${retryAttempt}: retrying in ${waitms}ms`
                            );
                            return timer(waitms);
                        }
                    })
                )
            )
        );
    }
}
